import { useT } from "@/shared/i18n/i18n";
"use client"

import { CalendarIcon, Clock, X } from "lucide-react"
import * as React from "react"
import type { DateRange } from "react-day-picker"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

type BaseProps = {
  align?: "center" | "end" | "start"
  /** Accessible name for the trigger button (e.g. the field name). */
  ariaLabel?: string
  /** "dropdown" shows month + year selectors; "label" shows plain month title. */
  captionLayout?: "dropdown" | "label"
  className?: string
  clearable?: boolean
  disabled?: boolean
  minuteStep?: number
  numberOfMonths?: number
  placeholder?: string
  /** 0 = Sunday … 6 = Saturday. Defaults to Monday. */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  withTime?: boolean
}

export type DatePickerProps =
  | (BaseProps & { mode?: "single"; onChange: (value: Date | undefined) => void; value?: Date })
  | (BaseProps & { mode: "range"; onChange: (value: DateRange | undefined) => void; value?: DateRange })

function pad(value: number) {
  return String(value).padStart(2, "0")
}

function timeString(date: Date | undefined) {
  return date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : ""
}

function withTimeApplied(date: Date, time: string) {
  const [hStr, mStr] = time.split(":")
  const next = new Date(date)
  next.setHours(Number(hStr) || 0, Number(mStr) || 0, 0, 0)
  return next
}

function formatDate(date: Date, withTime: boolean) {
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) + (withTime ? `, ${timeString(date)}` : "")
}

export function DatePicker(props: DatePickerProps) {
  const t = useT();
  const { align = "start", ariaLabel, captionLayout = "dropdown", className, clearable = true, disabled, minuteStep = 5, placeholder = "—", weekStartsOn = 1, withTime = false } = props
  const isRange = props.mode === "range"
  const numberOfMonths = props.numberOfMonths ?? (isRange ? 2 : 1)
  const [open, setOpen] = React.useState(false)
  const thisYear = new Date().getFullYear()
  const startMonth = new Date(thisYear - 100, 0)
  const endMonth = new Date(thisYear + 10, 11)

  // In-progress selection lives here so a half-picked range still highlights, even when
  // the consumer chooses not to persist partial values. Re-syncs from `value` while closed.
  const [draft, setDraft] = React.useState<Date | DateRange | undefined>(props.value)
  React.useEffect(() => {
    if (!open) setDraft(props.value)
  }, [open, props.value])

  const rangeValue = isRange ? (draft as DateRange | undefined) : undefined
  const singleValue = !isRange ? (draft as Date | undefined) : undefined
  const hasValue = isRange ? Boolean(rangeValue?.from) : Boolean(singleValue)

  const label = React.useMemo(() => {
    if (isRange) {
      if (!rangeValue?.from) return null
      const from = formatDate(rangeValue.from, withTime)
      const to = rangeValue.to ? formatDate(rangeValue.to, withTime) : "…"
      return `${from} – ${to}`
    }
    return singleValue ? formatDate(singleValue, withTime) : null
  }, [isRange, rangeValue, singleValue, withTime])

  const clear = () => {
    setDraft(undefined)
    if (props.mode === "range") props.onChange(undefined)
    else props.onChange(undefined)
    setOpen(false)
  }

  const commitSingle = (day: Date | undefined) => {
    if (props.mode === "range") return
    const next = day && withTime ? withTimeApplied(day, timeString(singleValue) || "09:00") : day
    setDraft(next)
    props.onChange(next)
    if (!withTime) setOpen(false)
  }

  // Push the range to the consumer. A lone start collapses to a single-day range so the
  // value stays well-formed (the field model wants exactly two dates, never a half range).
  const commitRangeDraft = (range: DateRange | undefined) => {
    if (props.mode !== "range") return
    if (!range?.from) {
      props.onChange(undefined)
      return
    }
    props.onChange(range.to ? range : { from: range.from, to: range.from })
  }

  // We drive range accumulation ourselves instead of react-day-picker's `addToRange`, which
  // completes a one-day range on the very first click. Each click only touches the local draft;
  // nothing is committed until the popover closes, so the popover never re-renders itself shut
  // mid-selection and a fresh click always starts a new range.
  const pickRange = (day: Date | undefined) => {
    if (props.mode !== "range" || !day) return
    const current = rangeValue
    // No start yet, or a finished range → begin a new one.
    if (!current?.from || (current.from && current.to)) {
      setDraft({ from: day, to: undefined })
      return
    }
    // Have a start, need an end → close the range, ordered.
    const from = current.from
    setDraft(day < from ? { from: day, to: from } : { from, to: day })
  }

  const closeRange = () => {
    commitRangeDraft(rangeValue)
    setOpen(false)
  }

  return (
    <Popover onOpenChange={(next) => { if (!next && isRange) commitRangeDraft(rangeValue); setOpen(next) }} open={open}>
      <PopoverTrigger
        aria-label={ariaLabel ? (label ? `${ariaLabel}: ${label}` : ariaLabel) : undefined}
        className={cn(
          "flex w-full min-w-0 items-center gap-1.5 rounded-md text-left text-sm transition-colors hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        disabled={disabled}
        onKeyDown={(event) => {
          // The visual "×" is a span nested in the trigger (a button cannot nest a
          // button), so keyboard users clear with Delete/Backspace instead.
          if (clearable && hasValue && (event.key === "Delete" || event.key === "Backspace")) {
            event.preventDefault()
            clear()
          }
        }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <CalendarIcon className="size-3.5 shrink-0 text-muted-foreground/70" />
        <span className={cn("min-w-0 flex-1 truncate", !label && "text-muted-foreground/70")}>{label ?? placeholder}</span>
        {clearable && hasValue ? (
          <span
            aria-label={t("datePicker.clear")}
            className="grid size-4 shrink-0 place-items-center rounded-sm text-muted-foreground/70 hover:text-foreground"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              clear()
            }}
            role="button"
          >
            <X className="size-3.5" />
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto gap-0 overflow-hidden p-0">
        {isRange ? (
          <Calendar captionLayout={captionLayout} endMonth={endMonth} mode="range" numberOfMonths={numberOfMonths} onSelect={(_range, triggerDate) => pickRange(triggerDate)} selected={rangeValue} startMonth={startMonth} weekStartsOn={weekStartsOn} />
        ) : (
          <Calendar captionLayout={captionLayout} endMonth={endMonth} mode="single" numberOfMonths={numberOfMonths} onSelect={commitSingle} selected={singleValue} startMonth={startMonth} weekStartsOn={weekStartsOn} />
        )}

        {withTime && hasValue ? (
          <div className="flex flex-col gap-2 border-t border-border/60 p-3">
            {isRange ? (
              <div className="grid grid-cols-2 gap-2">
                <TimeField
                  disabled={!rangeValue?.from}
                  label={t("datePicker.start")}
                  minuteStep={minuteStep}
                  onChange={(time) => rangeValue?.from && setDraft({ ...rangeValue, from: withTimeApplied(rangeValue.from, time) })}
                  value={timeString(rangeValue?.from)}
                />
                <TimeField
                  disabled={!rangeValue?.to}
                  label={t("datePicker.end")}
                  minuteStep={minuteStep}
                  onChange={(time) => rangeValue?.to && setDraft({ ...rangeValue, to: withTimeApplied(rangeValue.to, time) })}
                  value={timeString(rangeValue?.to)}
                />
              </div>
            ) : (
              <TimeField
                label={t("datePicker.time")}
                minuteStep={minuteStep}
                onChange={(time) => {
                  if (!singleValue) return
                  const next = withTimeApplied(singleValue, time)
                  setDraft(next)
                  props.onChange(next)
                }}
                value={timeString(singleValue)}
              />
            )}
          </div>
        ) : null}

        {(isRange || withTime) && hasValue ? (
          <div className="flex items-center justify-between gap-2 border-t border-border/60 p-2">
            <Button onClick={clear} size="sm" type="button" variant="ghost">{t("datePicker.clear")}</Button>
            <Button onClick={isRange ? closeRange : () => setOpen(false)} size="sm" type="button">{t("datePicker.done")}</Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

function TimeField({ disabled, label, minuteStep, onChange, value }: { disabled?: boolean; label: string; minuteStep: number; onChange: (value: string) => void; value: string }) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-[0.7rem] font-medium text-muted-foreground">{label}</span>
      <div className="flex h-8 items-center gap-1.5 rounded-md border border-input bg-transparent px-2 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30">
        <Clock className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          className="w-full min-w-0 bg-transparent text-sm outline-none [&::-webkit-calendar-picker-indicator]:hidden"
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          step={minuteStep * 60}
          type="time"
          value={value}
        />
      </div>
    </label>
  )
}
